import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KanbanTask } from '@/types';
import { Button } from '@/components/ui/Button';
import { Shield, Users, Save, X, ChevronDown } from 'lucide-react';

interface EditGroceryItemProps {
  item: KanbanTask;
  onSave: (taskId: string, newTitle: string, newQuantity: number, newUnit: string, newScope: 'personal' | 'household') => void;
  onCancel: () => void;
  currentHouseholdId: string | null;
}

export const EditGroceryItem: React.FC<EditGroceryItemProps> = ({
  item,
  onSave,
  onCancel,
  currentHouseholdId,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(item.title);
  const [quantity, setQuantity] = useState(item.consumption?.quantity || 1);
  const [unit, setUnit] = useState(item.consumption?.unit || 'pcs');
  const [scope, setScope] = useState<'personal' | 'household'>(item.scope || (currentHouseholdId ? 'household' : 'personal'));

  const handleSave = () => {
    onSave(item.id, title, quantity, unit, scope);
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 focus:border-brand focus:outline-none font-bold text-slate-900 dark:text-white"
        placeholder={t('common.item_name', 'Item Name')}
      />

      <div className="flex items-center gap-2">
        {/* Quantity */}
        <div className="flex-1 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-2 flex items-center">
          <span className="text-2xs font-black text-slate-400 dark:text-slate-300 uppercase tracking-tighter w-12">{t('common.qty', 'Qty')}</span>
          <input
            type="number"
            value={quantity}
            min={0.1}
            step={0.1}
            onChange={(e) => setQuantity(Number(e.target.value))}
            className="w-full bg-transparent border-none focus:ring-0 font-black text-slate-800 dark:text-white text-center"
          />
        </div>

        {/* Unit */}
        <div className="flex-1 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-2 flex items-center relative">
          <span className="text-2xs font-black text-slate-400 dark:text-slate-300 uppercase tracking-tighter w-12">{t('common.unit', 'Unit')}</span>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="w-full bg-transparent border-none focus:ring-0 font-black text-slate-800 dark:text-white text-center appearance-none cursor-pointer pr-8"
          >
            {['pcs', 'kg', 'oz', 'ltr', 'gal', 'pack', 'doz', 'box'].map(u => (
              <option key={u} value={u} className="dark:text-slate-700">{u}</option>
            ))}
          </select>
          <ChevronDown className="w-3 h-3 absolute right-3 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Scope Selector */}
      {currentHouseholdId && (
        <div className="flex bg-slate-50 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => setScope('personal')}
            className={`flex-1 px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-all ${scope === 'personal' ? 'bg-white dark:bg-slate-700 text-emerald-600 shadow-sm' : 'text-slate-400'}`}
          >
            <Shield className="w-4 h-4" />
            <span className="text-2xs font-black uppercase">Personal</span>
          </button>
          <button
            type="button"
            onClick={() => setScope('household')}
            className={`flex-1 px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-all ${scope === 'household' ? 'bg-white dark:bg-slate-700 text-brand shadow-sm' : 'text-slate-400'}`}
          >
            <Users className="w-4 h-4" />
            <span className="text-2xs font-black uppercase">Shared</span>
          </button>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button
          onClick={onCancel}
          variant="secondary"
          className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600"
        >
          <X className="w-4 h-4 mr-2" />
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          className="bg-brand hover:bg-brand-hover text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {t('common.save', 'Save')}
        </Button>
      </div>
    </div>
  );
};
